import React, { useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import DescriptionIcon from '@mui/icons-material/Description'
import { smbsApi, SMBDetailData } from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'
import { MAX_DOCUMENT_SIZE_LABEL } from '../../constants/uploads'
import { validatePdfFile, getFileNameFromPath } from '../../utils/uploads'
import DocumentFilePickerButton from '../DocumentFilePickerButton'

interface BusinessDocumentUploadProps {
  smb: SMBDetailData
  onReload?: (smbId: string) => void | Promise<void>
  onFileSelect?: (file: File | null) => void
}

export const BusinessDocumentUpload: React.FC<BusinessDocumentUploadProps> = ({ smb, onReload, onFileSelect }) => {
  const { showNotification } = useNotification()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [selectedDoc, setSelectedDoc] = useState<File | null>(null)
  const [docUploading, setDocUploading] = useState(false)
  const [docUploadProgress, setDocUploadProgress] = useState<number | null>(null)
  const [docUploadedLabel, setDocUploadedLabel] = useState<string | null>(null)

  const currentDocLabel = useMemo(() => {
    return (
      smb.document_name ||
      getFileNameFromPath(smb.document_original_path) ||
      null
    )
  }, [smb.document_name, smb.document_original_path])

  const handleDocChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validationError = validatePdfFile(file)
    if (validationError) {
      showNotification(validationError, 'error')
      event.target.value = ''
      return
    }

    setSelectedDoc(file)
    if (onFileSelect) {
      onFileSelect(file)
    }
  }

  const handleUploadDoc = async () => {
    if (!smb.id || !selectedDoc) return
    setDocUploading(true)
    setDocUploadProgress(null)
    try {
      await smbsApi.createUpdateSMB({ smb_id: smb.id }, {
        file: selectedDoc, onUploadProgress: (evt) => {
          const total = evt?.total ?? 0
          const loaded = evt?.loaded ?? 0
          if (total > 0) {
            setDocUploadProgress(Math.round((loaded / total) * 100))
          }
        }
      })

      setDocUploadedLabel(selectedDoc.name)
      setSelectedDoc(null)
      if (onFileSelect) {
        onFileSelect(null)
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      showNotification('Document uploaded successfully. (Existing document, if any, was replaced.)', 'success')
      if (onReload) {
        await onReload(smb.id)
      }
    } catch (error: any) {
      console.error('Error uploading SMB document:', error)
      showNotification(error?.response?.data?.detail || 'Failed to upload document.', 'error')
    } finally {
      setDocUploading(false)
      setDocUploadProgress(null)
    }
  }

  const onFileLabel = currentDocLabel || docUploadedLabel

  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
            Document status
          </Typography>
          {onFileLabel ? (
            <Chip
              icon={<DescriptionIcon fontSize="small" />}
              label={onFileLabel}
              color="success"
              variant="outlined"
              size="small"
              sx={{ 
                maxWidth: { xs: 180, sm: 220 },
                minWidth: 0,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }
              }}
              title={onFileLabel}
            />
          ) : (
            <Chip label="No document" color="warning" variant="outlined" size="small" />
          )}
        </Stack>

        {onFileLabel && (
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Uploading a new file will replace the existing document (only one document is stored per business).
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DocumentFilePickerButton
              file={selectedDoc}
              inputRef={fileInputRef}
              onChange={handleDocChange}
              disabled={!smb.id || docUploading}
              emptyLabel="📄 Choose PDF"
              helperText=""
            />
          </Box>
          <Button
            variant="contained"
            disabled={!selectedDoc || docUploading || !smb.id}
            onClick={handleUploadDoc}
            sx={{
              height: 40,
              px: 2.5,
              borderRadius: 1.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minWidth: { sm: 110 }
            }}
          >
            {docUploading ? <CircularProgress size={18} thickness={6} /> : currentDocLabel ? 'Replace' : 'Upload'}
          </Button>
        </Stack>

        {docUploading && (
          <Box sx={{ pt: 0.25 }}>
            <LinearProgress
              variant={docUploadProgress != null ? 'determinate' : 'indeterminate'}
              value={docUploadProgress ?? undefined}
            />
            {docUploadProgress != null && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Uploading… {docUploadProgress}%
              </Typography>
            )}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary">
          Supported: PDF (Max {MAX_DOCUMENT_SIZE_LABEL})
        </Typography>
      </Stack>
    </Paper>
  )
}

export default BusinessDocumentUpload
